import { User, Trash } from 'lucide-react';
import * as React from 'react';
import { getTranslate } from 'react-localize-redux';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';
import { AUsers, isOwner, useMe } from '../../../atoms/global/users';
import { isSuperAdmin } from '../../../components_v2/utils';
import Avatar from '../../../components_v2/avatar/Avatar';
import { getUserColor } from '../../../components_v2/avatar/Data/Data';
import { PaginationResult } from '../../../components_v2/pagination/model/Model';
import Pagination from '../../../components_v2/pagination/Pagination';
import { ButtonStyle } from '../../../components_v2/popup/PopupCreation';
import { Column, Table } from '../../../components_v2/table/Table';
import storeLang from '../../../helpers/storeLang';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { BorderColor2, GreenSidely, RedSidely } from '../../../styles/global/css/Utils';
import { Translate, translateToString } from '../../../styles/global/translate';
import { ToolbarBox } from '../../globals/defaultToolbar/style/Style';
import { ToolbarState } from '../../globals/mainPage/mainPage';
import { TableRowTitle } from '../../orders/templateOrders/style/Style';
import PermissionContext from '../../permissions/PermissionContext';
import useAlert from '../../alert/UseAlert';
import { LoadingStateEnum } from '../../import/model';
import { ComponentLoader } from '../../map/modalRight/ModalCalendar';
import Restricted from '../../permissions/Restricted';
import { ModalState } from '../../products/model';
import { AlertRes } from '../../alert/AlertProvider';
import { launchToastError, launchToastMessage } from '../../forms/visual-form-editor/visualFormEditor';
import { getTeams, Team } from '../../teams/actions';
import { CompanySubscriptions, deleteUserTotp, getCompanySubscriptions, getGroups, getMembers } from '../action';
import { DEFAULT_STEP, Group, MemberRow } from '../model';
import { ProfilePic, RightContainer, TableRow, UserPhoto } from '../style';
import { exportMembers, importMembers } from './importMembers';
import UserPopup from './UserPopup';
import { TOOLBARS_HEIGHT } from '../../globals/defaultToolbar/style/Style';

const MemberStatus = styled.div<{ active: boolean }>`
	border-radius: 4px;
	padding: 0.25em;
	color: white;
	min-width: 6em;
	text-align: center;
	background-color: ${p => p.active ? GreenSidely : BorderColor2};
`;

const Button = styled(DefaultButton)`
	margin: 0.5em;
`;

export default function Members(props: {
	setToolBarState: (value: ToolbarState) => void
}): JSX.Element {
	const [members, setMembers] = React.useState<MemberRow[]>([]);
	const [pagination, setPagination] = React.useState<PaginationResult>({ currentPage: 1, offset: 0, step: DEFAULT_STEP });
	const [modalState, setModalState] = React.useState<ModalState<number>>({ isOpen: false });
	const [importing, setImporting] = React.useState<boolean>(false);
	const [groups, setGroups] = React.useState<Group[]>();
	const [teams, setTeams] = React.useState<Team[]>();
	const [users, setUsers] = useRecoilState(AUsers);

	const { isAllowedTo } = React.useContext(PermissionContext);
	const alert = useAlert();
	const me = useMe();
	const admin = isSuperAdmin();
	// resetting someone's two-factor lowers their sign-in requirements, so
	// within an account it stays with its owners; superadmins reach it like
	// everything else, and cerberus enforces the same pair
	const canResetTotp = isOwner(me) || admin;
	const translate = getTranslate(storeLang.getState().localize);

	const [companySubscriptions, setCompanySubscriptions] = React.useState<CompanySubscriptions>();

	React.useEffect(() => {
		getCompanySubscriptions().then((res) => setCompanySubscriptions(res ?? {}));
	}, []);

	React.useEffect(() => {
		const seatNumber = (companySubscriptions?.annual_seats ?? 0) + (companySubscriptions?.monthly_seats ?? 0) + (companySubscriptions?.free_seats ?? 0);
		const memberLength = members.filter(u => u.status).length;
		props.setToolBarState({
			bottomLeftToolbarComponent: companySubscriptions ? <ToolbarBox padding='0 10px'>
				<User />
				<NoMarginP><Translate id='invoiced_seats' /> :</NoMarginP>
				<NoMarginP>{(companySubscriptions.annual_seats ?? 0) + (companySubscriptions.monthly_seats ?? 0)}</NoMarginP>
				{companySubscriptions.free_seats && companySubscriptions.free_seats !== 0 && <>-
					<NoMarginP><Translate id='free_seats' /> :</NoMarginP>
					<NoMarginP>{(companySubscriptions.free_seats ?? 0)}</NoMarginP>
				</>}
				-
				<NoMarginP><Translate id='active_seats' /> :</NoMarginP>
				<NoMarginP style={{ color: seatNumber < memberLength ? RedSidely : undefined }}>{memberLength} {seatNumber < memberLength ? <Translate id='upcoming_regularization' /> : ''}</NoMarginP>
				-
				<NoMarginP><Translate id='available_seats' /> :</NoMarginP>
				<NoMarginP>{seatNumber - memberLength < 0 ? 0 : seatNumber - memberLength}</NoMarginP>
			</ToolbarBox> : undefined,
			bottomRightToolbarComponent: <>
				<Button buttonStyle={ButtonStyle.Default} onClick={exportMembers}>
					<Translate id='export' />
				</Button>
				<Restricted to={{ objectAction: 'CreateUserManagementSettings' }}>
					<Button buttonStyle={ButtonStyle.Default} onClick={() => importMembers(alert, setImporting, () => getUsers())}>
						<Translate id='general.import' />
					</Button>
				</Restricted>
				<Restricted to={{ objectAction: 'CreateUserManagementSettings' }} fallback={
					<Restricted to={{ objectAction: 'UpdateAccountSettings' }}>
						<Button
							buttonStyle={ButtonStyle.Default}
							onClick={() => window.open('https://share-eu1.hsforms.com/1ssVkenwpSDmDdryffBsesgfgz7f', '_blank')?.focus()}
						>
							<Translate id='create_user' />
						</Button>
					</Restricted>
				}>
					<Button
						buttonStyle={ButtonStyle.Default}
						onClick={() => setModalState({ isOpen: true })}
					>
						<Translate id='create_user' />
					</Button>
				</Restricted>
			</>
		});
	}, [members, companySubscriptions]);

	const getUsers = async() => await getMembers(pagination.step, pagination.offset)
		.then(res => setMembers(res.data))
		.catch(console.log);

	React.useEffect(() => {
		getGroups().then(res => setGroups(res.data));
		getTeams().then(setTeams);
	}, []);

	React.useEffect(() => {
		getUsers();
	}, [pagination]);

	const isAllowedToUpdateUser = isAllowedTo({ objectAction: 'UpdateUserManagementSettings' });

	const columns: Array<Column<MemberRow>> = React.useMemo(
		() => [
			{
				id: 'photo',
				Header: ' ',
				accessor: (row: MemberRow) => row.photo
					? <ProfilePic>
						<UserPhoto src={row.photo} />
					</ProfilePic>
					: <Avatar userId={row.id} name={row.name} width={'30px'} fontSize={'13px'} />,
				width: 51,
				disableSortBy: true,
				unresizeable: true
			},
			{
				id: 'name',
				Header: translate('admin.table.name') as string,
				accessor: (row: MemberRow) => <TableRowTitle onClick={isAllowedToUpdateUser ? () => setModalState({ isOpen: true, data: row.id }) : undefined}>{row.name}</TableRowTitle>,
				width: undefined
			},
			{
				id: 'email',
				Header: translate('admin.table.email') as string,
				accessor: (row: MemberRow) => <TableRow>{row.email}</TableRow>,
				width: undefined
			},
			{
				id: 'phone',
				Header: translate('admin.table.phone') as string,
				accessor: (row: MemberRow) => <TableRow>{row.phone}</TableRow>,
				width: undefined
			},
			{
				id: 'groups',
				Header: translate('admin.title') as string,
				accessor: (row: MemberRow) => {
					let display = row.group_info?.[0]?.[1] ?? '';
					if (row.group_info?.length > 1) {
						if (display.length > 15) {
							display = display.substring(0, 15) + '...';
						}
						display += ', +' + (row.group_info.length - 1);
					} else if (display.length > 20) {
						display = display.substring(0, 15) + '...';
					}

					if (row.is_owner) {
						return <TableRow>-</TableRow>;
					}

					return <TableRow>{display}</TableRow>;
				},
				width: undefined
			},
			{
				id: 'status',
				Header: translate('admin.table.status') as string,
				accessor: (row: MemberRow) => <MemberStatus active={row.status}>{translateToString(row.status ? 'active' : 'inactive')}</MemberStatus>,
				width: undefined
			},
			...(canResetTotp
				? [{
					id: 'totp',
					Header: translateToString('admin.table.two_factor'),
					accessor: (row: MemberRow) => row.has_totp
						? <TableRow
							cursor='pointer'
							title={translateToString('reset_two_factor')}
							onClick={() => alert({
								title: translateToString('reset_two_factor'),
								content: <div>{translateToString('reset_two_factor_message', [['NAME', row.name]])}</div>,
								width: '500px',
								mode: 'delete',
							}).then(res => {
								if (res.res !== AlertRes.Ok) return;
								deleteUserTotp(row.id)
									.then(() => {
										launchToastMessage(translateToString('reset_two_factor_done'));
										setMembers(ms => ms.map(m => m.id === row.id ? { ...m, has_totp: false } : m));
									})
									.catch(e => {
										console.error(e);
										launchToastError(translateToString('error_please_retry'));
									});
							})}
						><Trash /></TableRow>
						: <TableRow>-</TableRow>,
					width: 120,
					disableSortBy: true,
				}]
				: []),
		],
		[members, canResetTotp]
	);

	return (
		<RightContainer>
			{importing && <ComponentLoader loadingState={LoadingStateEnum.LOADING} allScreen />}
			<Table
				noBorder
				height={`calc(100vh - ${TOOLBARS_HEIGHT + 50}px)`}
				columns={columns}
				data={members}
				onCellClicked={(value) => {
					const row = members.find(m => m.id === value.row.original.id);
					if ((row == null) || !value.column.editable) return;
					const newRow = { ...row };
					newRow.edit = value.column.id;
					setMembers(members.map(m => m.id == row.id ? newRow : m));
				}}
			/>
			<Pagination
				label='Users'
				amount={members[0]?.count ?? 0}
				steps={[DEFAULT_STEP, 100, 200, 500]}
				onChange={(value) => setPagination(value)}
			/>
			<UserPopup isOpen={modalState.isOpen} onClose={(user) => {
				if (user) {
					const memberIndex = members.findIndex(m => m.id === user.id);
					if (memberIndex !== -1) {
						members[memberIndex] = {
							...members[memberIndex],
							...user.user,
							group_info: user.user.groups?.map(group => [group, groups?.find(g => g.id === group)?.name ?? 'Unknown']) ?? []
						};
					} else {
						members.push({
							...user.user,
							name: user.user.name!,
							status: user.user.status!,
							is_owner: user.user.is_owner!,
							has_totp: false,
							id: user.id,
							count: 0,
							group_info: user.user.groups?.map(group => [group, groups?.find(g => g.id === group)?.name ?? 'Unknown']) ?? []
						});
					}
					const userIndex = users.findIndex(u => u.id === user.id);
					if (userIndex !== -1) {
						setUsers(users.map(u => u.id === user.id ? {
							...u,
							...user.user,
						} : u));
					} else {
						setUsers([...users, {
							...user.user,
							id: user.id,
							name: user.user.name!,
							status: user.user.status!,
							email: user.user.email!,
							phone: user.user.phone ?? '',
							color: user.user.color ?? getUserColor(user.user.name!),
							photoUrl: '',
							roleId: user.user.is_owner ? 1 : 2,
							roleName: user.user.is_owner ? 'Owner' : 'Admin'
						}]);
					}

					setMembers([...members]);
				}
				setModalState({ isOpen: false });
			}} userId={modalState.data} groups={groups} teams={teams} />
		</RightContainer>
	);
}

export const NoMarginP = styled.p`
	margin: 0;
`;

export const NoMarginH1 = styled.h1`
	margin: 0;
`;

export const NoMarginH2 = styled.h2`
	margin: 0;
`;

export const NoMarginH3 = styled.h3`
	margin: 0;
`;

export const NoMarginH4 = styled.h4`
	margin: 0;
`;

export const NoMarginH5 = styled.h5`
	margin: 0;
`;

export const NoMarginH6 = styled.h6`
	margin: 0;
`;
